<template>
  <div class="tiptap-editor">
    <!-- Toolbar -->
    <div v-if="showToolbar" class="editor-toolbar bg-white border border-gray-200 rounded-t-lg p-3">
      <div class="flex flex-wrap items-center gap-2">
        <!-- Text Formatting -->
        <div class="flex items-center gap-1 border-r border-gray-200 pr-2">
          <button
            @click="editor?.chain().focus().toggleBold().run()"
            :class="{ 'bg-teal-100 text-teal-700': editor?.isActive('bold') }"
            class="toolbar-btn"
            type="button"
            title="Bold"
          >
            <span class="font-bold">B</span>
          </button>
          <button
            @click="editor?.chain().focus().toggleItalic().run()"
            :class="{ 'bg-teal-100 text-teal-700': editor?.isActive('italic') }"
            class="toolbar-btn"
            type="button"
            title="Italic"
          >
            <span class="italic">I</span>
          </button>
          <button
            @click="editor?.chain().focus().toggleUnderline().run()"
            :class="{ 'bg-teal-100 text-teal-700': editor?.isActive('underline') }"
            class="toolbar-btn"
            type="button"
            title="Underline"
          >
            <span class="underline">U</span>
          </button>
        </div>

        <!-- Headings -->
        <div class="flex items-center gap-1 border-r border-gray-200 pr-2">
          <button
            @click="editor?.chain().focus().toggleHeading({ level: 1 }).run()"
            :class="{ 'bg-teal-100 text-teal-700': editor?.isActive('heading', { level: 1 }) }"
            class="toolbar-btn"
            type="button"
            title="Heading 1"
          >
            H1
          </button>
          <button
            @click="editor?.chain().focus().toggleHeading({ level: 2 }).run()"
            :class="{ 'bg-teal-100 text-teal-700': editor?.isActive('heading', { level: 2 }) }"
            class="toolbar-btn"
            type="button"
            title="Heading 2"
          >
            H2
          </button>
          <button
            @click="editor?.chain().focus().toggleHeading({ level: 3 }).run()"
            :class="{ 'bg-teal-100 text-teal-700': editor?.isActive('heading', { level: 3 }) }"
            class="toolbar-btn"
            type="button"
            title="Heading 3"
          >
            H3
          </button>
        </div>

        <!-- Lists -->
        <div class="flex items-center gap-1 border-r border-gray-200 pr-2">
          <button
            @click="editor?.chain().focus().toggleBulletList().run()"
            :class="{ 'bg-teal-100 text-teal-700': editor?.isActive('bulletList') }"
            class="toolbar-btn"
            type="button"
            title="Bullet List"
          >
            <ListBulletIcon class="w-4 h-4" />
          </button>
          <button
            @click="editor?.chain().focus().toggleOrderedList().run()"
            :class="{ 'bg-teal-100 text-teal-700': editor?.isActive('orderedList') }"
            class="toolbar-btn"
            type="button"
            title="Ordered List"
          >
            <numbered-list class="w-4 h-4">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7M4 5v14M4 12h7" />
              </svg>
            </numbered-list>
          </button>
        </div>

        <!-- Text Alignment -->
        <div class="flex items-center gap-1 border-r border-gray-200 pr-2">
          <button
            @click="editor?.chain().focus().setTextAlign('left').run()"
            :class="{ 'bg-teal-100 text-teal-700': editor?.isActive({ textAlign: 'left' }) }"
            class="toolbar-btn"
            type="button"
            title="Align Left"
          >
            <Bars3BottomLeftIcon class="w-4 h-4" />
          </button>
          <button
            @click="editor?.chain().focus().setTextAlign('center').run()"
            :class="{ 'bg-teal-100 text-teal-700': editor?.isActive({ textAlign: 'center' }) }"
            class="toolbar-btn"
            type="button"
            title="Align Center"
          >
            <Bars3Icon class="w-4 h-4" />
          </button>
          <button
            @click="editor?.chain().focus().setTextAlign('right').run()"
            :class="{ 'bg-teal-100 text-teal-700': editor?.isActive({ textAlign: 'right' }) }"
            class="toolbar-btn"
            type="button"
            title="Align Right"
          >
            <Bars3BottomRightIcon class="w-4 h-4" />
          </button>
        </div>

        <!-- Links and Images -->
        <div class="flex items-center gap-1 border-r border-gray-200 pr-2">
          <button
            @click="setLink"
            :class="{ 'bg-teal-100 text-teal-700': editor?.isActive('link') }"
            class="toolbar-btn"
            type="button"
            title="Add Link"
          >
            <LinkIcon class="w-4 h-4" />
          </button>
          <button
            @click="$emit('add-image')"
            class="toolbar-btn"
            type="button"
            title="Add Image"
          >
            <PhotoIcon class="w-4 h-4" />
          </button>
        </div>

        <!-- Actions -->
        <div class="flex items-center gap-1">
          <button
            @click="editor?.chain().focus().undo().run()"
            :disabled="!editor?.can().undo()"
            class="toolbar-btn disabled:opacity-50 disabled:cursor-not-allowed"
            type="button"
            title="Undo"
          >
            <ArrowUturnLeftIcon class="w-4 h-4" />
          </button>
          <button
            @click="editor?.chain().focus().redo().run()"
            :disabled="!editor?.can().redo()"
            class="toolbar-btn disabled:opacity-50 disabled:cursor-not-allowed"
            type="button"
            title="Redo"
          >
            <ArrowUturnRightIcon class="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>

    <!-- Editor Content -->
    <div 
      class="editor-content bg-white border border-gray-200 rounded-b-lg"
      :class="{ 'rounded-lg': !showToolbar }"
    >
      <EditorContent
        :editor="editor"
        class="prose prose-lg max-w-none p-6 min-h-[400px] focus:outline-none"
      />
    </div>

    <!-- Character Count (optional) -->
    <div v-if="showWordCount && editor" class="mt-2 text-sm text-gray-500 text-right">
      {{ editor.storage.characterCount?.characters() || 0 }} characters,
      {{ editor.storage.characterCount?.words() || 0 }} words
    </div>
  </div>
</template>

<script setup>
import { watch, onBeforeUnmount } from 'vue'
import { Editor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Image from '@tiptap/extension-image'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import Underline from '@tiptap/extension-underline'
import TextAlign from '@tiptap/extension-text-align'
import Heading from '@tiptap/extension-heading'
import {
  ListBulletIcon,
  LinkIcon,
  PhotoIcon,
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  Bars3BottomLeftIcon,
  Bars3Icon,
  Bars3BottomRightIcon
} from '@heroicons/vue/24/outline'

// Props
const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: 'Escribe el contenido de tu artículo...'
  },
  showToolbar: {
    type: Boolean,
    default: true
  },
  showWordCount: {
    type: Boolean,
    default: true
  },
  editable: {
    type: Boolean,
    default: true
  }
})

// Emits
const emit = defineEmits(['update:modelValue', 'add-image', 'content-changed'])

// Create editor instance
const editor = new Editor({
  content: props.modelValue,
  editable: props.editable,
  extensions: [
    StarterKit.configure({
      heading: false // We'll configure this separately
    }),
    Heading.configure({
      levels: [1, 2, 3, 4, 5, 6]
    }),
    Image.configure({
      HTMLAttributes: {
        class: 'max-w-full h-auto rounded-lg mx-auto block'
      }
    }),
    Link.configure({
      openOnClick: false,
      HTMLAttributes: {
        class: 'text-teal-600 hover:text-teal-700 underline'
      }
    }),
    Placeholder.configure({
      placeholder: props.placeholder
    }),
    Underline,
    TextAlign.configure({
      types: ['heading', 'paragraph']
    })
  ],
  onUpdate: ({ editor }) => {
    const content = editor.getHTML()
    emit('update:modelValue', content)
    emit('content-changed', {
      content,
      wordCount: editor.storage.characterCount?.words() || 0,
      characterCount: editor.storage.characterCount?.characters() || 0
    })
  },
  editorProps: {
    attributes: {
      class: 'prose prose-lg max-w-none focus:outline-none'
    }
  }
})

// Watch for external content changes
watch(() => props.modelValue, (newValue) => {
  if (editor && editor.getHTML() !== newValue) {
    editor.commands.setContent(newValue)
  }
})

// Watch for editable changes
watch(() => props.editable, (newValue) => {
  if (editor) {
    editor.setEditable(newValue)
  }
})

// Link functionality
const setLink = () => {
  const previousUrl = editor.getAttributes('link').href
  const url = window.prompt('URL:', previousUrl)

  // cancelled
  if (url === null) {
    return
  }

  // empty
  if (url === '') {
    editor.chain().focus().extendMarkRange('link').unsetLink().run()
    return
  }

  // update link
  editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
}

// Cleanup
onBeforeUnmount(() => {
  if (editor) {
    editor.destroy()
  }
})

// Expose editor instance
defineExpose({
  editor,
  insertImage: (src, alt = '') => {
    if (editor) {
      editor.chain().focus().setImage({ src, alt }).run()
    }
  },
  getContent: () => editor?.getHTML() || '',
  setContent: (content) => editor?.commands.setContent(content),
  focus: () => editor?.commands.focus()
})
</script>

<style scoped>
@reference "../../assets/styles/main.css";

/* Toolbar button styling */
.toolbar-btn {
  @apply px-2 py-1 rounded text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors duration-200 text-sm font-medium flex items-center justify-center min-w-[32px] min-h-[32px];
}

.toolbar-btn:hover {
  @apply transform scale-105;
}

/* Editor content styling */
.editor-content :deep(.ProseMirror) {
  outline: none;
  padding: 1rem;
  min-height: 400px;
}

.editor-content :deep(.ProseMirror p.is-editor-empty:first-child::before) {
  color: #9ca3af;
  content: attr(data-placeholder);
  float: left;
  height: 0;
  pointer-events: none;
}

/* Prose styling adjustments */
.editor-content :deep(.prose) {
  max-width: none;
  color: #374151;
}

.editor-content :deep(.prose h1) {
  @apply text-3xl font-bold text-gray-900 tracking-tight mb-4;
}

.editor-content :deep(.prose h2) {
  @apply text-2xl font-bold text-gray-900 tracking-tight mb-3;
}

.editor-content :deep(.prose h3) {
  @apply text-xl font-semibold text-gray-900 mb-3;
}

.editor-content :deep(.prose p) {
  @apply text-gray-700 leading-relaxed mb-4;
}

.editor-content :deep(.prose ul) {
  @apply list-disc list-inside mb-4;
}

.editor-content :deep(.prose ol) {
  @apply list-decimal list-inside mb-4;
}

.editor-content :deep(.prose blockquote) {
  @apply border-l-4 border-teal-500 pl-4 italic text-gray-600 mb-4;
}

.editor-content :deep(.prose img) {
  @apply rounded-lg shadow-md;
}

/* Link styling */
.editor-content :deep(.prose a) {
  @apply text-teal-600 hover:text-teal-700 underline;
}

/* Focus ring */
.editor-content:focus-within {
  @apply ring-2 ring-teal-500/50;
}

/* Text alignment classes */
.editor-content :deep(.prose .text-left) {
  text-align: left;
}

.editor-content :deep(.prose .text-center) {
  text-align: center;
}

.editor-content :deep(.prose .text-right) {
  text-align: right;
}
</style>
